import KurentoRtspSource from './KurentoRtspSource/KurentoRtspSource';

import { GENERAL } from 'config';
import MediaMtxSource from './MediaMtxSource/MediaMtxSource';
import { store } from 'store/store';
import { selectStreamManagerToken } from 'store/authSlice/authSelectors';
import { ACTION_NAMES } from './KurentoRtspSource/ActionScheduler/configuration';
import { isCameraStreamLogsEnabled } from './config';
import FreezeDetector from './FreezeDetector/FreezeDetector';
import { webRtcTelemetry } from 'services/StreamingObservability/WebRtcTelemetryService';

class CameraStreamManager {
    constructor() {
        this.#sources = new Map();
        this.#sourcesByVideoOutputId = new Map();
        this.#sourcesByTab = new Map();
        this.#eventHandlers = new Map();
        this.#freezeDetector = new FreezeDetector();
    }

    #eventHandlers;
    #sources;
    #sourcesByVideoOutputId;
    #sourcesByTab;
    #tempWebRTCEndpointIdList = [];
    #freezeDetector;
    #freezeDetectorStarted = false;

    // out of all sources, a single one, on a single tab, will be enabled for audio
    #sourceWithAudio;

    create(camConfig, preconfigure) {
        try {
            const source = this.#createSourceIfNeeded(camConfig);
            if (this.#shouldConfigureStream(source, camConfig)) {
                return this.#configureStreamIfNeeded(source, camConfig, preconfigure);
            }
        } catch (err) {
            this.#handle(err, `create()`);
        }
    }

    #createSourceIfNeeded(camConfig) {
        const { camId: cameraId, kurentoId, mtxDirect } = camConfig;
        let source = this.#sources.get(cameraId);

        if (!source) {
            source = mtxDirect ? this.#createMediaMtxSource(camConfig) : this.#createKurentoSource(kurentoId, cameraId);
            this.#sources.set(cameraId, source);
        }

        return source;
    }

    #createMediaMtxSource(camConfig) {
        const source = new MediaMtxSource(camConfig);
        source.activateStreamType();
        return source;
    }

    #createKurentoSource(kurentoId, cameraId) {
        return new KurentoRtspSource(kurentoId, cameraId);
    }

    #shouldConfigureStream(source, camConfig) {
        const { videoData, mtxDirect } = camConfig;
        if (mtxDirect) return false;
        return !source.playerByStreamType(videoData.streamType);
    }

    #configureStreamIfNeeded(source, camConfig, preconfigure) {
        const { camId: cameraId, videoData } = camConfig;
        const streamType = videoData?.streamType;

        if (preconfigure) {
            this.#log(`create(): Preconfiguring camId=${cameraId} streamType=${streamType}`);
            return source.scheduleAndWait(ACTION_NAMES.CONFIGURE_CAMERA_STREAM, camConfig, false, preconfigure);
        }

        return source.schedule(ACTION_NAMES.CONFIGURE_CAMERA_STREAM, camConfig, false, preconfigure);
    }

    async activate(cameraId, streamType, videoOutputId, tabId, transactionId, isTabActive) {
        try {
            const source = this.#sources.get(cameraId);
            this.#add(tabId, videoOutputId, source);
            source.addVideoOutput(videoOutputId, transactionId);
            this.#startFreezeDetectorIfNeeded();
            this.#freezeDetector.register(source);
            if (isTabActive || (!source.activeStreamType && !source.activatingStreamType))
                return source.scheduleAndWait(ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE, streamType);
        } catch (err) {
            this.#handle(err, `activate()`);
        }
    }

    deactivate(videoOutputId) {
        try {
            const source = this.#get(videoOutputId);
            source.schedule(ACTION_NAMES.STOP_CAMERA_STREAM);
            if (source?.cameraId != null) this.#freezeDetector.unregister(source.cameraId);
        } catch (err) {
            this.#handle(err, `playStreamType()`);
        }
    }

    #startFreezeDetectorIfNeeded() {
        if (this.#freezeDetectorStarted) return;
        this.#freezeDetectorStarted = true;
        this.#freezeDetector.start();
    }

    playStreamType(streamType, videoOutputId) {
        try {
            if (!streamType) {
                this.#log(`playStreamType(): Stream type not provided, returning`, 'error');
                return;
            }
            const source = this.#get(videoOutputId);
            if (!source?.playerByStreamType(streamType)) {
                this.#log(
                    `playStreamType(): Camera stream id=${source.cameraId} does not have relevant player for stream type ${streamType}`,
                    'error'
                );
                return;
            }
            source.schedule(ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE, streamType);
        } catch (err) {
            this.#handle(err, `playStreamType()`);
        }
    }

    async removeVideoOutput(videoOutputId, force = true) {
        try {
            const source = this.#get(videoOutputId, force);
            source && (await source.removeVideoOutput(videoOutputId));
            this.#detachVideoOutputFromIndexes(videoOutputId);
        } catch (err) {
            this.#handle(err, `removeVideoOutput()`);
        }
    }

    #detachVideoOutputFromIndexes(videoOutputId) {
        this.#sourcesByVideoOutputId.delete(videoOutputId);
        for (const [tabId, ids] of this.#sourcesByTab.entries()) {
            const idx = ids.indexOf(videoOutputId);
            if (idx === -1) continue;
            ids.splice(idx, 1);
            if (!ids.length) this.#sourcesByTab.delete(tabId);
            break;
        }
    }

    getTransactionIdsByCameraId(cameraId) {
        return this.#sources.get(cameraId)?.getTransactionIds() || [];
    }

    isActiveOnVideoWall(cameraId) {
        return this.#sources.get(cameraId)?.isActiveOnVideoWall();
    }

    registerEventHandler(eventType, handler) {
        const handlerPresent = this.#eventHandlers.get(eventType);
        if (handlerPresent) return;
        this.#eventHandlers.set(eventType, handler);
    }

    setBeforeUnloadListener() {
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    removeBeforeUnloadListener() {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    beforeUnloadHandler = (e) => {
        e?.preventDefault();
        this.#freezeDetector.stop();
        this.#freezeDetectorStarted = false;
        webRtcTelemetry.flush({ keepalive: true });
        const webRtcEndpointIds = cameraStreamManager.getWebRTCEndpointIds();
        const streamManagerToken = selectStreamManagerToken(store.getState());

        if (webRtcEndpointIds.length) {
            this.#log(`beforeUnloadHandler(): Sending WebRTCEndpointId list for releasing ids=[${webRtcEndpointIds}]`);
            const data = { webRtcEndpointIds, streamManagerToken };
            const sent = navigator.sendBeacon(`${GENERAL.ADDRESSES.HTTP.MAIN}/camera-stream/release-endpoints`, JSON.stringify(data));
            this.#log(`beforeUnloadHandler():WebRTCEndpointIds ${sent ? '' : 'NOT '}sent!`);
        } else {
            this.#log(`beforeUnloadHandler(): No WebRTCEndpointIds for release ${webRtcEndpointIds}`);
        }
    };

    #add(tabId, videoOutputId, source) {
        this.#sourcesByVideoOutputId.set(videoOutputId, source.cameraId);
        let byTab = this.#sourcesByTab.get(tabId) || [];
        if (!byTab.length) this.#sourcesByTab.set(tabId, byTab);
        byTab.push(videoOutputId);
        this.#log(`Added source: tabId=${tabId}, videoOutputId=${videoOutputId}`);
    }

    #get(id, required = true) {
        try {
            const cameraId = typeof id === 'number' ? id : this.#sourcesByVideoOutputId.get(id);
            const result = cameraId && this.#sources.get(cameraId);

            if (required && !result) {
                throw new Error(`Invalid source id="${id}"`);
            }

            return result;
        } catch (err) {
            this.#handle(err, `#get()`, true);
        }
    }

    handleKurentoInactive(kurentoId) {
        const sources = [...this.#sources.values()];
        sources.forEach((source) => {
            if (source.kurentoId !== kurentoId) return;
            source.invalidateStreamConfig();
        });
    }

    getWebRTCEndpointIds() {
        const sources = [...this.#sources.values()];
        const ids = sources.reduce((ids, source) => {
            const id = source.webRtcEndpointId;
            id && ids.push(id);
            return ids;
        }, []);
        return [...ids, ...this.#tempWebRTCEndpointIdList];
    }

    getLocalIceCandidatesUsed() {
        const sources = [...this.#sources.values()];
        const ips = sources.reduce((ips, source) => {
            const ipArray = source.localICECandidatesUsed;
            ipArray && ipArray.forEach((element) => ips.push(element));
            return ips;
        }, []);
        return [...new Set(ips)];
    }

    // Access by tab and id

    getIds() {
        return Array.from(this.#sourcesByVideoOutputId.keys());
    }

    getTabs() {
        return Array.from(this.#sourcesByTab.keys());
    }

    getIdsByTab(tabId) {
        const byTab = this.#sourcesByTab.get(tabId) || [];
        return byTab;
    }

    // Actions on videos per id

    restart(id, streamType) {
        try {
            const source = this.#get(id);
            source.schedule(ACTION_NAMES.SOFT_REFRESH_CAMERA_STREAM, streamType);
        } catch (err) {
            this.#handle(err, 'restart()');
        }
    }

    async hardRefresh(id, forceRefreshMode = true) {
        try {
            const source = this.#get(id);
            await source.hardRefresh(forceRefreshMode);
        } catch (err) {
            this.#handle(err, 'hardRefresh()');
        }
    }

    restartAll() {
        if (!this.#sources.size) return;
        this.#log(`restartAll(): Restarting all streams`);
        this.#sources?.forEach(({ activeStreamType }, key) => {
            this.restart(key, activeStreamType);
        });
    }

    restartTab(tabId, hardRefresh = false) {
        if (!this.#sourcesByTab.get(tabId)?.length) return;
        this.#log(`Refreshing streams for tabId=${tabId} refreshType=${hardRefresh ? 'HARD' : 'SOFT'}`);
        this.#sourcesByTab.get(tabId)?.forEach((videoOutputId) => {
            if (hardRefresh) this.hardRefresh(videoOutputId, false);
            else {
                const source = this.#get(videoOutputId);
                this.restart(videoOutputId, source?.activeStreamType);
            }
        });
    }

    async updateCameraConfig(camConfig) {
        try {
            const source = this.#sources.get(camConfig.camId);
            if (source) {
                await source.scheduleAndWait(ACTION_NAMES.CONFIGURE_CAMERA_STREAM, camConfig, true);
            }
        } catch (err) {
            this.#handle(err, 'updateCameraConfig()');
        }
    }

    // Statuses of videos per id

    getInfo(id) {
        try {
            const source = this.#get(id, false);
            return source ? source.getInfo() : '<loading>';
        } catch (err) {
            this.#handle(err, `getInfo(${id})`);
        }
    }

    // Actions on videos per tab

    activateTab(tabId) {
        const sourceIds = this.getIdsByTab(tabId);
        if (sourceIds.length > 0) {
            this.#log(`Activated tabId=${tabId} with audio videoOutputId=${sourceIds[0]}`);
            this.enableAudio(sourceIds[0]);
        }
    }

    async closeTab(tabId, transactionId) {
        try {
            const sourceIds = this.getIdsByTab(tabId);
            if (!sourceIds.length) return;

            for (const id of sourceIds) {
                try {
                    const source = this.#get(id);
                    source.removeVideoOutput(id);
                    this.#sourcesByVideoOutputId.delete(id);
                    // eslint-disable-next-line
                } catch (_) {
                    this.#log(`VideoOutputId ${id} already removed, continuing...`);
                }
            }

            this.#sourcesByTab.delete(tabId);

            const transactionsInfo = transactionId ? ` transactionId=${transactionId}` : '';
            this.#log(`Removed streams from video elements for tabId=${tabId}${transactionsInfo}: ${sourceIds}`);
        } catch (err) {
            this.#handle(err, `closeTab(${tabId},${transactionId})`);
        }
    }

    async stopAllStreams() {
        try {
            const sources = this.#sources;
            this.#tempWebRTCEndpointIdList = this.getWebRTCEndpointIds();

            this.#sources = new Map();
            this.#sourcesByVideoOutputId = new Map();
            this.#sourcesByTab = new Map();
            this.#sourceWithAudio = null;

            for (const source of [...sources.values()]) {
                if (source?.cameraId != null) this.#freezeDetector.unregister(source.cameraId);
                await source.scheduleAndWait(ACTION_NAMES.RELEASE_CAMERA_STREAM, true);
            }
            this.#tempWebRTCEndpointIdList = [];

            this.#log(`Stopped and removed all videos`);
        } catch (err) {
            this.#handle(err, `stopAllStreams()`);
        }
    }

    async stopStreamsForTab(tabId, transactionId) {
        try {
            const sources = this.#sources;
            const cameraIdsToRemove = [];
            for (const [cameraId, source] of [...sources.entries()]) {
                if (source.isExclusivelyUsedForTransactionId(transactionId)) {
                    this.#freezeDetector.unregister(cameraId);
                    await source.scheduleAndWait(ACTION_NAMES.RELEASE_CAMERA_STREAM, true);
                    cameraIdsToRemove.push(cameraId);
                }
            }

            for (const videoOutputId of this.#sourcesByTab.get(tabId) || []) {
                const cameraId = this.#sourcesByVideoOutputId.get(videoOutputId);
                this.#sourcesByVideoOutputId.delete(videoOutputId);
                if (!cameraIdsToRemove.includes(cameraId)) {
                    const source = this.#sources.get(cameraId);
                    source.removeVideoOutput(videoOutputId);
                    continue;
                }
                this.#sources.delete(cameraId);
            }
            this.#sourcesByTab.delete(tabId);

            const transactionInfo = transactionId ? ` transactionId=${transactionId}` : '';
            this.#log(`Closed WebRTC connections for streams exclusively used on tabId=${tabId}${transactionInfo}`);
        } catch (err) {
            this.#handle(err, `stopStreamsForTab(${tabId},${transactionId})`);
        }
    }

    async closeAllTabsExcept(tabId) {
        try {
            const tabs = this.getTabs();
            for (const tab of tabs) {
                if (tab !== tabId) {
                    await this.closeTab(tab);
                }
            }
            this.#log(`Closed all tabs except tabId=${tabId}`);
        } catch (err) {
            this.#handle(err, `closeAllTabsExcept(${tabId})`);
        }
    }

    async stopAllStreamsExcludeTab(tabId) {
        try {
            const videoOutputIds = this.#sourcesByTab.get(tabId);

            if (videoOutputIds === undefined) {
                this.stopAllStreams();
                return;
            }

            const sources = new Map();
            const sourcesByTab = new Map();
            const sourcesByVideoOutputId = new Map();

            sourcesByTab.set(tabId, videoOutputIds);
            const sourcesToKeep = [];
            const keepIdsBySource = new Map();

            for (let videoOutputId of videoOutputIds) {
                const sourceId = this.#sourcesByVideoOutputId.get(videoOutputId);
                const source = this.#sources.get(sourceId);
                if (!sources.has(sourceId)) {
                    sourcesToKeep.push(source);
                    sources.set(sourceId, source);
                    keepIdsBySource.set(source, []);
                }
                keepIdsBySource.get(source).push(videoOutputId);
                sourcesByVideoOutputId.set(videoOutputId, sourceId);
            }

            for (let [source, keepIds] of keepIdsBySource.entries()) {
                source.makeExclusiveForVideoOutputs(keepIds);
            }

            for (let source of [...this.#sources.values()]) {
                if (!sourcesToKeep.includes(source)) {
                    if (source?.cameraId != null) this.#freezeDetector.unregister(source.cameraId);
                    await source.scheduleAndWait(ACTION_NAMES.RELEASE_CAMERA_STREAM, true);
                }
            }

            this.#sources = sources;
            this.#sourcesByTab = sourcesByTab;
            this.#sourcesByVideoOutputId = sourcesByVideoOutputId;

            this.#log(`Closed WebRTC connections for all streams except those used on tabId=${tabId}`);
        } catch (err) {
            this.#handle(err, `stopAllStreamsExcludeTab(${tabId})`);
        }
    }

    //AoT

    // keepActiveStreamType: a camera that is already playing keeps its stream type, so warming it up for AoT does not flip
    // transaction tiles that play SUB (transactionSubStreamConfigEnabled) to MAIN; an idle camera is started as before
    async activateAot(camConfig, { keepActiveStreamType = false } = {}) {
        try {
            await this.create(camConfig, true);
            const cameraId = camConfig.camId;
            const streamType = camConfig?.videoData?.streamType;
            let source = this.#sources.get(cameraId);
            source.aot = true;
            if (keepActiveStreamType && (source.activeStreamType || source.activatingStreamType)) return;
            this.playStreamType(streamType, cameraId);
        } catch (err) {
            this.#handle(err, `activateAot()`);
        }
    }

    async cancelAot(camConfig) {
        try {
            const cameraId = camConfig.camId;
            let source = this.#sources.get(cameraId);
            if (!source) return;
            source.aot = false;
            const isActivelyUsed = !!source.getTransactionIds().length;
            if (!isActivelyUsed) this.deactivate(cameraId);
        } catch (err) {
            this.#handle(err, `cancelAot()`);
        }
    }

    // Audio

    enableAudio(id, muteAll = false) {
        if (!id) return;

        if (this.#sourceWithAudio) {
            if (this.#sourceWithAudio.hasVideoOutputId(id) || muteAll) {
                this.#sourceWithAudio.audio(false);
                this.#sourceWithAudio = null;
                return;
            }

            this.#sourceWithAudio.audio(false);
        }

        if (muteAll) return;

        this.#sourceWithAudio = this.#get(id);
        this.#sourceWithAudio.audio(true);
    }

    isAudioEnabled(id) {
        if (!id || !this.#sourceWithAudio) return;
        return this.#sourceWithAudio.hasVideoOutputId(id);
    }

    audioOff() {
        this.#sourceWithAudio?.audio(false);
        this.#sourceWithAudio = undefined;
    }

    getSourceWithAudio() {
        return this.#sourceWithAudio;
    }

    getVideoElementId(tabId, camId) {
        return 'tc-cs-cam-' + tabId + '-' + camId;
    }

    // String representation

    toString() {
        const arr = Array.from(this.#sourcesByVideoOutputId.values());
        return `{CameraStreamManager sources=[${arr}]}`;
    }
    #log(message, mode = 'log') {
        if (!isCameraStreamLogsEnabled(mode)) return;
        console[mode](`[CameraStreamManager] ${message}`);
    }

    #handle(err, message, rethrow = false) {
        if (err) {
            this.#log(`ERROR: ${message}: ${err?.message || err}`, 'error');
            if (rethrow) throw new Error(err?.message || err);
        }
    }
}

export const cameraStreamManager = new CameraStreamManager();

if (import.meta.env.VITE_ENV !== 'production') window.cameraStreamManager = cameraStreamManager;
